// frontend/src/theme.js
// Brand tokens for الو یارا — mirrors the approved design artifact
// (deep pine primary, warm gold accent, warm-paper background).

import { createTheme } from '@mui/material/styles';

export const brand = {
    paper: '#F4F5F0',
    paperRaised: '#FFFFFF',
    ink: '#16221D',
    inkSoft: '#4C5C54',
    inkFaint: '#7C8A82',
    primary: '#1F5D50',
    primaryDeep: '#0F332B',
    primaryTint: '#E4EEE9',
    heroText: '#F5F3EA',
    accent: '#DE9C4C',
    accentDeep: '#A9702A',
    accentTint: '#FBEDD8',
    line: '#DCE1DA',
    fontDisplay: "'Vazirmatn', Tahoma, sans-serif",
    fontQuote: "'Noto Naskh Arabic', 'Vazirmatn', serif",
};

// Glassmorphism tokens — used by the admin panel and dashboards: a soft
// gradient canvas behind frosted, translucent cards. Kept separate from
// `brand` so pages that want the flatter Phase-1 look can still opt out.
export const glass = {
    canvasGradient: `radial-gradient(1200px 600px at 10% -10%, ${brand.primaryTint} 0%, transparent 60%),
                      radial-gradient(1000px 500px at 100% 0%, ${brand.accentTint} 0%, transparent 55%),
                      linear-gradient(180deg, #EAF1EC 0%, #F4F5F0 40%)`,
    panelBg: 'rgba(255,255,255,0.86)',
    panelBgStrong: 'rgba(255,255,255,0.94)',
    panelBorder: '1px solid rgba(255,255,255,0.8)',
    panelShadow: '0 12px 40px rgba(15,51,43,0.10)',
    blur: 'blur(16px)',
    darkPanelBg: 'rgba(15,51,43,0.88)',
    darkPanelBorder: '1px solid rgba(255,255,255,0.08)',
    // NOTE: MUI's `sx` prop multiplies a *bare number* passed to `borderRadius`
    // by `theme.shape.borderRadius` (14 here) — so `borderRadius: 20` becomes
    // 280px, not 20px, and turns any real-sized card into an ellipse with the
    // corners eating into the content. Always use an explicit px STRING here.
    card: {
        background: 'rgba(255,255,255,0.86)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '18px',
        boxShadow: '0 12px 40px rgba(15,51,43,0.10)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 18px 48px rgba(15,51,43,0.16)' },
    },
    darkCard: {
        background: 'rgba(15,51,43,0.88)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '18px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    },
};

const theme = createTheme({
    direction: 'rtl',
    palette: {
        mode: 'light',
        primary: {
            main: brand.primary,
            dark: brand.primaryDeep,
            light: brand.primaryTint,
            contrastText: brand.heroText,
        },
        secondary: {
            main: brand.accent,
            dark: brand.accentDeep,
            light: brand.accentTint,
            contrastText: '#241505',
        },
        background: {
            default: brand.paper,
            paper: brand.paperRaised,
        },
        text: {
            primary: brand.ink,
            secondary: brand.inkSoft,
        },
        divider: brand.line,
    },
    shape: {
        borderRadius: 14,
    },
    typography: {
        fontFamily: brand.fontDisplay,
        h1: { fontWeight: 800 },
        h2: { fontWeight: 800 },
        h3: { fontWeight: 800 },
        h4: { fontWeight: 700 },
        h5: { fontWeight: 700 },
        h6: { fontWeight: 700 },
        button: { fontWeight: 700, textTransform: 'none' },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 999, paddingInline: 22, paddingBlock: 11,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                    '&:hover': { transform: 'translateY(-2px)' },
                    '&:active': { transform: 'translateY(0) scale(0.97)' },
                    '&.Mui-disabled': { transform: 'none' },
                },
                sizeLarge: { paddingInline: 28, paddingBlock: 13, fontSize: '1rem' },
                contained: { '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 24px rgba(15,51,43,0.25)' } },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: { backgroundColor: brand.primaryDeep },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    transition: 'transform 0.15s ease',
                },
                clickable: {
                    '&:hover': { transform: 'translateY(-1px)' },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { transform: 'translateY(-3px)' },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    transition: 'transform 0.15s ease',
                    '&:hover': { transform: 'scale(1.12)' },
                },
            },
        },
    },
});

export default theme;
